import React from "react";
import BlinkingComponent from "../BlinkingComponent/BlinkingComponent";
import HidingButton from "../HidingButton/HidingButton";

interface AnonymousBalancePanelProps {
    readonly anonymous: true;
    readonly balance: number;
    readonly currency: string;
    readonly balanceTextFormat: (value: number) => string;
    readonly className?: string;
}

interface LoggedInBalancePanelProps {
    readonly anonymous: false;
    readonly balance: number;
    readonly balanceHiddenText: string;
    readonly currency: string;
    readonly userLogin: string;
    readonly foreignLogin: boolean;
    readonly balanceTextFormat: (value: number) => string;
    readonly className?: string;
}
export type BalancePanelProps =
    | LoggedInBalancePanelProps
    | AnonymousBalancePanelProps;

export interface BalancePanelState {
    readonly visible: boolean;
    readonly blinking: boolean;
}

export default class BalancePanel extends React.Component<
    BalancePanelProps,
    BalancePanelState
> {
    private readonly BALANCE_DISPLAY_TIMEOUT_LONG = 10000;
    private readonly BALANCE_DISPLAY_TIMEOUT_SHORT = 3000;
    private readonly BLINKING_TIME = 20000;

    private visibilityTimeout?: number;
    private blinkingTimeout?: number;

    constructor(props: BalancePanelProps) {
        super(props);
        this.state = { visible: false, blinking: false };
    }

    public componentDidMount() {
        this.showBalance(this.BALANCE_DISPLAY_TIMEOUT_SHORT);
    }

    public componentDidUpdate(prevProps: BalancePanelProps) {
        if (this.props.balance !== prevProps.balance) {
            this.showBalance(this.BALANCE_DISPLAY_TIMEOUT_LONG);
        } else if (this.didUserChange(prevProps)) {
            this.showBalance(this.BALANCE_DISPLAY_TIMEOUT_SHORT);
        }
        this.handleBlinking(prevProps.balance, this.props.balance);
    }

    public componentWillUnmount() {
        if (this.blinkingTimeout !== null) {
            window.clearTimeout(this.blinkingTimeout);
        }
        if (this.visibilityTimeout !== null) {
            window.clearTimeout(this.visibilityTimeout);
        }
    }

    public render() {
        if (this.props.anonymous) {
            return (
                <BlinkingComponent
                    value={this.props.balance}
                    formatValue={this.props.balanceTextFormat}
                    blinking={this.state.blinking}
                    className={this.props.className}
                />
            );
        } else {
            return (
                <HidingButton
                    onShow={this.onHiddenBalanceClick}
                    title={this.props.balanceHiddenText}
                    hidden={!this.state.visible}
                    className={this.props.className}
                >
                    <BlinkingComponent
                        value={this.props.balance}
                        formatValue={this.props.balanceTextFormat}
                        blinking={this.state.blinking}
                        className={this.props.className}
                    />
                </HidingButton>
            );
        }
    }

    private handleBlinking = (oldBalance: number, newBalance: number) => {
        if (oldBalance > newBalance) {
            if (this.blinkingTimeout != null) {
                window.clearTimeout(this.blinkingTimeout);
            }
            this.setState({ blinking: true });
            this.blinkingTimeout = window.setTimeout(
                () => this.setState({ blinking: false }),
                this.BLINKING_TIME
            );
        } else if (oldBalance < newBalance) {
            if (this.blinkingTimeout != null) {
                window.clearTimeout(this.blinkingTimeout);
                this.setState({ blinking: false });
            }
        }
    };

    private showBalance = (time: number) => {
        this.setState({ visible: true });

        if (this.visibilityTimeout !== null) {
            window.clearTimeout(this.visibilityTimeout);
        }
        this.visibilityTimeout = window.setTimeout(
            () => this.setState({ visible: false }),
            time
        );
    };

    private onHiddenBalanceClick = () =>
        this.showBalance(this.BALANCE_DISPLAY_TIMEOUT_LONG);

    private didUserChange = (prevProps: BalancePanelProps): boolean => {
        return (
            this.props.anonymous !== prevProps.anonymous ||
            (!this.props.anonymous &&
                !prevProps.anonymous &&
                this.props.userLogin !== prevProps.userLogin)
        );
    };
}
